import React from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../Context/AuthContext";
import MessageRcs from "../../../campaigns/rcs/Message";
import { useFormik, FormikProvider } from "formik";
import { Device } from "../../../campaigns/rcs/DevicePreview";
import { errorLabels } from "../../../../utils/helpers";
import { ErrorMessage, SuccessMessage } from "../../../../utils/toastify";
import { testValidationSchema } from "../../../campaigns/rcs/validationSchema";
import MessageText from "../../../campaigns/rcs/MessageText";
import { useEncrypt } from "../../../campaigns/hooks/useEncrypt";
import TestRCS from "../../../../utils/classes/test-message-rcs";
import axios from "axios";
import { ApiEndpoint, ApiRCSEndpoint } from "../../../../utils/ApiEndpoint";
import { httpClient } from "../../../../utils/HttpClient";
import _Button from "../../../generic/_Button";
import _Input from "../../../generic/_Input";
const mapExamples = {
  AUTHENTICATION: {
    step: 1,
    fallback:
      "Código de verificación: 123456. Este código expira en 5 minutos. No compartas este código con nadie.",
    rcsmessage:
      "Código de verificación: 123456. Este código expira en 5 minutos. No compartas este código con nadie.",
    buttonQuickReplies: [
      {
        type: "reply",
        text: "Verificar",
        action: "VERIFY",
      },
      {
        type: "reply",
        text: "Reenviar código",
        action: "RESEND_CODE",
      },
    ],
  },

  TRANSACTION: {
    step: 1,
    fallback:
      "Se ha realizado un cargo de $1,250.00 MXN en tu tarjeta terminación 1234 en SUPERMERCADO XYZ. ¿Autorizas esta transacción?",
    rcsmessage:
      "Se ha realizado un cargo de $1,250.00 MXN en tu tarjeta terminación 1234 en SUPERMERCADO XYZ. ¿Autorizas esta transacción?",
    buttonQuickReplies: [
      {
        type: "reply",
        text: "Autorizar",
        action: "AUTHORIZE",
      },
      {
        type: "reply",
        text: "Rechazar",
        action: "REJECT",
      },
      {
        type: "reply",
        text: "Reportar fraude",
        action: "REPORT_FRAUD",
      },
    ],
  },
  PREVENT_FRAUD: {
    step: 2,
    fallback:
      "Si rechazas alguna de las transacciones, tu tarjeta se bloqueara temporalmente",
    title_rcs_message:
      "Por seguridad de tu tarjeta, confirma las transacciones",
    imageUrl: "https://www.odt.com.mx/assets/img/hero-odt.png",
    height: "TALL",
    cardOrientation: "VERTICAL",
    rcsmessage:
      "Si rechazas alguna de las transacciones, tu tarjeta se bloqueara temporalmente",
    buttonActions: [
      {
        type: "openUrlAction",
        text: "Verificar ahora",
        action: "https://example.com/verify-now",
      },
    ],
  },

  PROMOTION: {
    step: 2,
    fallback:
      "OFERTA ESPECIAL: 30% de descuento en tu próxima compra. Código: PROMO30. Válido hasta el 30 de abril.",
    title_rcs_message: "OFERTA ESPECIAL",
    rcsmessage:
      "OFERTA ESPECIAL: 30% de descuento en tu próxima compra. Código: PROMO30. Válido hasta el 30 de abril.",
    imageUrl:
      "https://d1x6nshzzr4pih.cloudfront.net/files/images/794e206c-8e46-4144-8bfd-3b8cab968c97/nathan-dumlao-NZeMMrsUu7M-unsplash.jpg",
    height: "TALL",
    cardOrientation: "VERTICAL",
    buttonActions: [
      {
        type: "openUrlAction",
        text: "Comprar ahora",
        action: "https://example.com/ofertas",
      },
      {
        type: "dialAction",
        text: "Llamar a tienda",
        action: "9991234567",
      },
    ],
    buttonQuickReplies: [
      {
        type: "reply",
        text: "Guardar código",
        action: "SAVE_CODE",
      },
      {
        type: "reply",
        text: "Ver más ofertas",
        action: "MORE_OFFERS",
      },
    ],
  },

  SERVICEREQUEST: {
    step: 2,
    fallback:
      "Servicio técnico solicitado. Tu folio es: ST-2024-001. Un técnico estará en tu domicilio el día 15 de abril entre 10:00 y 14:00 hrs.",
    title_rcs_message: "Servicio técnico",
    rcsmessage:
      "Servicio técnico solicitado. Tu folio es: ST-2024-001. Un técnico estará en tu domicilio el día 15 de abril entre 10:00 y 14:00 hrs.",
    imageUrl: "https://www.odt.com.mx/assets/img/hero-odt.png",
    cardOrientation: "VERTICAL",
    height: "MEDIUM",
    buttonActions: [
      {
        type: "ViewLocationAction",
        text: "Ver ubicación",
        lat: "19.432608",
        long: "-99.133209",
        description: "Ver ubicación",
        action: "VIEW_LOCATION",
      },
      // {
      //   type: "openUrlAction",
      //   text: "Contactar soporte",
      //   action: "https://www.odt.com.mx",
      // },
    ],
    buttonQuickReplies: [
      {
        type: "reply",
        text: "Confirmar cita",
        action: "CONFIRM_APPOINTMENT",
      },
      {
        type: "reply",
        text: "Reagendar",
        action: "RESCHEDULE",
      },
      {
        type: "reply",
        text: "Cancelar servicio",
        action: "CANCEL_SERVICE",
      },
    ],
  },

  ACKNOWLEDGEMENT: {
    step: 1,
    fallback:
      "Hemos recibido tu mensaje. Te contactaremos en las próximas 24 horas. Folio de seguimiento: RCP-2024-0892",
    rcsmessage:
      "Hemos recibido tu mensaje. Te contactaremos en las próximas 24 horas. Folio de seguimiento: RCP-2024-0892",
    buttonQuickReplies: [
      {
        type: "reply",
        text: "Dar seguimiento",
        action: "TRACK",
      },
      {
        type: "reply",
        text: "Hablar con agente",
        action: "TALK_TO_AGENT",
      },
    ],
  },

  CAROUSEL: {
    step: 3,
    fallback: "Promociones  especiales en tu primera compra. Ver oferta",
    carrousel: [
      {
        title: "Tarjeta 1",
        description: "Promoción especial en tu primera compra",
        imageUrl: "https://www.odt.com.mx/assets/img/hero-odt.png",
        height: "MEDIUM",
        buttonActions: [
          {
            type: "openUrlAction",
            text: "Ver producto",
            action: "https://www.odt.com.mx",
          },
        ],
        buttonQuickReplies: [
          {
            type: "reply",
            text: "Comprar",
            action: "BUY_PRODUCT_1",
          },
          {
            type: "reply",
            text: "Más info",
            action: "MORE_INFO_1",
          },
        ],
      },
      {
        title: "Tarjeta 2",
        description: "Descuento exclusivo por tiempo limitado",
        imageUrl: "https://www.odt.com.mx/assets/img/hero-odt.png",
        height: "MEDIUM",
        buttonActions: [
          {
            type: "openUrlAction",
            text: "Ver oferta",
            action: "https://www.odt.com.mx",
          },
        ],
        buttonQuickReplies: [
          {
            type: "reply",
            text: "Usar código",
            action: "USE_CODE_2",
          },
          {
            type: "reply",
            text: "Ver más",
            action: "SEE_MORE_2",
          },
        ],
      },
      {
        title: "Tarjeta 3",
        description: "Contáctanos para más información",
        imageUrl: "https://www.odt.com.mx/assets/img/hero-odt.png",
        height: "TALL",
        buttonActions: [
          {
            type: "dialAction",
            text: "Llamar ahora",
            action: "9999509788",
          },
        ],
        buttonQuickReplies: [
          {
            type: "reply",
            text: "Hablar con asesor",
            action: "TALK_AGENT",
          },
        ],
      },
    ],
  },
};
const EXAMPLES_RCS = [
  {
    value: "AUTHENTICATION",
    label: "Autenticación",
    icon: "lock",
  },
  {
    value: "TRANSACTION",
    label: "Transacción",
    icon: "credit_card",
  },
  {
    value: "PROMOTION",
    label: "Promoción",
    icon: "percent_discount",
  },
  {
    value: "SERVICEREQUEST",
    label: "Servicio",
    icon: "service_toolbox",
  },
  {
    value: "ACKNOWLEDGEMENT",
    label: "Acknowledgement",
    icon: "check",
  },
  {
    value: "CAROUSEL",
    label: "Carrousel",
    icon: "view_carousel",
  },
  {
    value: "PREVENT_FRAUD",
    label: "Prevenir fraude",
    icon: "warning",
  },
];
const _ModalDevicesRcsAgent = (props) => {
  const { visible, data, onClose, title = "", isTestMessage = false } = props;
  const auth = useAuth();
  const { getEncrypt } = useEncrypt();
  const { uid_user, token } = auth.getSessionData();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      rcsmessage: "",
      title_rcs_message: "",
      rcstemplate: "",
      buttonActions: [],
      buttonQuickReplies: [],
      imageUrl: "",
      thumbnail_url: "",
      height: "",
      carrousel: [],
      step: 1,
      fallback: "",
      template_fallback: "",
      phone: data?.phoneNumber ? data.phoneNumber.replace("+52", "") : "",
    },
    validationSchema: testValidationSchema,
    validateOnMount: false,
    validateOnChange: true,
    onSubmit: async (values) => {
      const currentTime = Date.now();
      let dataSend = {
        security: {
          partner_id: partnerDetails?.vc_partnerid,
          time: currentTime,
          hash: getEncrypt(
            partnerDetails?.vc_partnerid,
            currentTime,
            partnerDetails?.vc_secretkey,
          ),
        },
        xtraparams: {
          source: "Test Panel",
          panelUserId: uid_user,
        },
        notify: {
          service_id: partnerDetails?.vc_serviceid,
          number: values.phone,
          message: "",
          channel: "RCS",
          rcsMessage: {},
        },
      };

      const Resolver = new TestRCS(values);

      const { rcsMessage, message } = Resolver.buildTestData();

      dataSend.notify.rcsMessage = rcsMessage;
      dataSend.notify.message = message;

      sendTestMessage(dataSend);
    },
  });

  const { mutate: sendTestMessage, isPending: isLoading } = useMutation({
    mutationKey: ["send_test_message"],
    mutationFn: async (values) => {
      const response = await axios({
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        url: ApiRCSEndpoint,
        data: values,
      });

      return response.data;
    },
    onSuccess: (response) => {
      if (response?.result?.code === "-9999") {
        throw new Error("Error al enviar el mensaje de prueba");
      }
      SuccessMessage("Mensaje enviado con éxito");
      formik.resetForm();
    },
    onError: (error) => {
      let message = error?.message || "Error al enviar el mensaje de prueba";
      ErrorMessage(`${message}`);
    },
  });

  const { data: partnerDetails } = useQuery({
    queryKey: ["partner", uid_user, data],
    queryFn: async () => {
      const partnerId = data?.partnerId ?? data?.uid_partner;
      const response = await httpClient(
        "GET",
        `${ApiEndpoint}administration/partnerdetail/${partnerId}`,
        {},
        token,
      );

      return response.data;
    },
    enabled: visible,
  });

  const _onClose = () => {
    if (onClose) onClose();
    formik.resetForm();
  };

  const onHandleChangeExamples = (exampleValue) => {
    formik.setValues({
      ...formik.initialValues,
      phone: formik.values.phone,
      ...mapExamples[exampleValue],
    });
  };

  return (
    <Modal
      show={visible}
      onHide={_onClose}
      centered
      size="xl"
      scrollable={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-x-hidden">
        <FormikProvider value={formik}>
          <div className="">
            <div className="font-semibold text-md">Ejemplos predefinidos:</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-4">
              {EXAMPLES_RCS.map((example, index) => (
                <div key={index}>
                  <button
                    className="rounded-lg border-none px-4 py-2 hover:bg-gray-100 text-left min-w-[200px] flex items-center justify-between gap-2"
                    onClick={() => {
                      onHandleChangeExamples(example.value);
                    }}
                  >
                    {example.label}
                    <span className="material-symbols-outlined">
                      {example.icon}
                    </span>
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                className="rounded-lg border-none px-4 py-2 bg-blue-200 hover:bg-gray-100 text-left w-[250px] flex items-center justify-between gap-2"
                onClick={() => {
                  formik.resetForm();
                }}
              >
                Limpiar
                <span className="material-symbols-outlined">delete</span>
              </button>
            </div>
          </div>

          <div className="w-full h-[2px] mt-[20px] mb-[20px] bg-gray-200"></div>
          <_Input
            title="Número de teléfono"
            value={formik.values.phone}
            id="phone"
            onChange={formik.handleChange}
            placeholder="Ej: 123456789"
            isError={formik.errors.phone}
            errors={formik.errors.phone ? formik.errors.phone : ""}
            isDisabled={!isTestMessage}
            required
          />

          <div className="w-full h-[2px] mt-[20px] mb-[20px] bg-gray-200"></div>

          <div className="grid grid-cols-12 gap-1">
            <div className="col-span-12 lg:col-span-8">
              <MessageRcs />
              <MessageText />
            </div>

            <div className="col-span-12 lg:col-span-4">
              <Device scale="0.60" position="left" />
            </div>
          </div>
        </FormikProvider>
      </Modal.Body>
      <Modal.Footer className="py-2 bg-white">
        <_Button
          onClick={_onClose}
          loading={isLoading}
          disabled={isLoading}
          style="none"
          title="Cancelar"
        />
        <_Button
          onClick={async () => {
            try {
              const valid = await formik.validateForm();

              if (Object.keys(valid).length !== 0) {
                throw new Error(`Campos inválidos: ${errorLabels(valid)}`);
              }
              await formik.submitForm();
            } catch (error) {
              ErrorMessage(error.message);
            }
          }}
          loading={isLoading}
          disabled={isLoading}
          style="primary"
          title="Enviar"
        />
      </Modal.Footer>
    </Modal>
  );
};

_ModalDevicesRcsAgent.propTypes = {
  visible: PropTypes.bool,
  data: PropTypes.object,
  onClose: PropTypes.func,
  title: PropTypes.string,
  isTestMessage: PropTypes.bool,
};

export default _ModalDevicesRcsAgent;
